import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import DisbursementInvoices from "./components/disbursement-invoices";
import DisbursementsFilterBar from "./components/disbursements-filter-bar";
import UploadUtrModal from "./components/upload-utr-modal";
import RevertPaymentModal from "./components/revert-payment-modal";
import useDisbursementQueue from "./hooks/use-disbursement-queue";
import { formatDate } from "@helpers/date";
import { formatCurrency } from "@helpers/utils";
import { DEBIT_NOTE_STATUS_LABEL, DEBIT_NOTE_STATUS_TONE } from "@constants/panel/common";

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

const Disbursements = () => {
    const {
        items, pagination, loading,
        filters, handleFilterChange,
        handlePageChange, handlePerRowsChange,
        utrTarget, revertTarget,
        openUtrModal, closeUtrModal, openRevertModal, closeRevertModal,
        uploadUTR, revertPayment
    } = useDisbursementQueue();

    const columns = [
        {
            Header  : "Debit note",
            accessor: "debit_note_number",
            Cell    : ({ value }) => <span className="font-mono text-xs text-gray-900">{value || "—"}</span>
        },
        {
            Header  : "Brand",
            accessor: "account_name",
            Cell    : ({ value, row }) => (
                <Link to={`/brands/details/${row.account_id}`} className="inline-flex items-center gap-1 font-medium text-accent hover:underline">
                    {value || "—"}
                    <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            )
        },
        {
            Header  : "Period",
            accessor: "payout_period",
            Cell    : ({ value }) => <span className="text-gray-800">{value ? formatDate(value, "MMMM YYYY") : "—"}</span>
        },
        {
            Header  : "Status",
            accessor: "status",
            Cell    : ({ value }) => <StatusChip tone={DEBIT_NOTE_STATUS_TONE[value] || "neutral"} label={DEBIT_NOTE_STATUS_LABEL[value] || value || "—"} />
        },
        {
            Header  : "Amount",
            accessor: "amount",
            Cell    : ({ value, row }) => <span className="font-medium text-gray-900">{formatCurrency(value, row.currency)}</span>
        },
        {
            Header  : "Publishers paid",
            accessor: "paid_publishers",
            Cell    : ({ value, row }) => {
                const progressPct = row.amount > 0 ? Math.round((row.paid_amount / row.amount) * 100) : 0;
                return (
                    <div className="min-w-[9rem]">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                            <div className="h-full rounded-full bg-navy" style={{ width: `${progressPct}%` }} />
                        </div>
                        <p className="mt-1 text-xs text-muted">
                            {value ?? 0} of {row.total_publishers ?? 0} · {formatCurrency(row.paid_amount, row.currency)} of {formatCurrency(row.amount, row.currency)}
                        </p>
                    </div>
                );
            }
        },
        {
            Header  : "Brand approved",
            accessor: "approved_at",
            Cell    : ({ value }) => <span className="text-gray-800">{formatDate(value)}</span>
        }
    ];

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">Disbursement queue</h1>
            <p className="mt-1 text-sm text-muted">Brand-approved debit notes. Confirm funds, then record payments to mark publishers as Paid.</p>

            <DisbursementsFilterBar filters={filters} onFilterChange={handleFilterChange} />

            <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={columns}
                    data={items}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    expandableRows
                    expandableRowsComponent={({ data }) => (
                        <DisbursementInvoices
                            item={data}
                            onRecordPayment={openUtrModal}
                            onRevertPayment={openRevertModal} />
                    )}
                    customNoData={<span className="text-sm text-muted">Nothing waiting on disbursement right now.</span>}
                />
            </div>

            <UploadUtrModal
                open={Boolean(utrTarget)}
                onClose={closeUtrModal}
                publisherName={utrTarget?.holder_name}
                amount={utrTarget?.amount}
                onConfirm={uploadUTR} />

            <RevertPaymentModal
                open={Boolean(revertTarget)}
                onClose={closeRevertModal}
                publisherName={revertTarget?.holder_name}
                amount={revertTarget?.amount}
                onRevert={revertPayment} />
        </div>
    );
};

export default Disbursements;
